import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { generateBreadcrumbLD } from '@/components/feature/Breadcrumb';
import { getComicById } from '@/mocks/comics';
import NotFound from '@/pages/NotFound';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = useMemo(() => getComicById(id || ''), [id]);

  if (!comic) {
    return <NotFound />;
  }

  const formatViews = (views: number) => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: comic.category, href: `/category/${comic.categorySlug}` },
    { label: comic.title },
  ];

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <article>
            <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-6">
              <Breadcrumb items={breadcrumbItems} />

              <div className="mt-8 flex flex-col md:flex-row gap-8 md:gap-10">
                <div className="flex-shrink-0 w-full md:w-[300px]">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200 sticky top-24">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 韩漫漫画在线阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {comic.isFree && (
                      <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                        免费阅读
                      </span>
                    )}
                    {comic.isHot && (
                      <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                        热门推荐
                      </span>
                    )}
                    {comic.isNew && (
                      <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                        新作上线
                      </span>
                    )}
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      comic.status === '连载中'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-background-200 text-foreground-600'
                    }`}>
                      {comic.status}
                    </span>
                  </div>

                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                    {comic.title}
                  </h1>
                  <p className="text-sm text-foreground-500 mb-6">
                    作者：{comic.author} · {comic.category} · 共 {comic.chapters.length} 话
                  </p>

                  <div className="flex flex-wrap items-center gap-4 mb-6">
                    <div className="flex items-center gap-1.5">
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map(i => (
                          <i
                            key={i}
                            className={`text-sm ${i <= Math.round(comic.rating / 2) ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                          ></i>
                        ))}
                      </div>
                      <span className="text-sm font-semibold text-accent-600">{comic.rating}</span>
                    </div>
                    <span className="text-sm text-foreground-400">|</span>
                    <span className="text-sm text-foreground-500">
                      <i className="ri-eye-line mr-1"></i>
                      {formatViews(comic.views)} 阅读
                    </span>
                    <span className="text-sm text-foreground-400">|</span>
                    <span className="text-sm text-foreground-500">
                      <i className="ri-heart-line mr-1"></i>
                      {formatViews(comic.favorites)} 收藏
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {comic.tags.map(tag => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="bg-background-100/70 rounded-xl p-5 mb-8">
                    <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {comic.description}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 mb-8">
                    <button
                      type="button"
                      className="px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-line mr-1.5"></i>
                      开始阅读第一话
                    </button>
                    <button
                      type="button"
                      className="px-6 py-3 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-heart-add-line mr-1.5"></i>
                      加入收藏
                    </button>
                  </div>
                </div>
              </div>

              <section className="mt-10 mb-16">
                <h2 className="text-xl font-heading text-foreground-900 mb-6 flex items-center gap-2">
                  <i className="ri-list-check text-accent-500"></i>
                  章节列表
                  <span className="text-sm font-normal text-foreground-500 font-sans">
                    （共 {comic.chapters.length} 话）
                  </span>
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
                  {comic.chapters.map(chapter => (
                    <div
                      key={chapter.id}
                      className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer group"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        {chapter.isFree ? (
                          <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                            免费
                          </span>
                        ) : (
                          <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-background-200 text-foreground-500 text-xs whitespace-nowrap">
                            VIP
                          </span>
                        )}
                        <span className="text-sm text-foreground-800 truncate group-hover:text-primary-600 transition-colors">
                          {chapter.title}
                        </span>
                      </div>
                      <span className="flex-shrink-0 text-xs text-foreground-400 ml-2">
                        {chapter.updatedAt}
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              <div className="text-center pb-6">
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{comic.category}分类
                </Link>
              </div>
            </div>

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify([
                  {
                    '@context': 'https://schema.org',
                    '@type': 'Article',
                    headline: `${comic.title} - 韩漫漫画在线阅读`,
                    description: comic.description,
                    author: {
                      '@type': 'Person',
                      name: comic.author,
                    },
                    datePublished: comic.chapters[0].updatedAt,
                    dateModified: comic.updatedAt,
                    url: `https://www.manhuakr.com/comic/${comic.id}`,
                    image: comic.cover,
                    publisher: {
                      '@type': 'Organization',
                      name: '韩漫漫画',
                    },
                  },
                  {
                    '@context': 'https://schema.org',
                    '@type': 'CreativeWork',
                    name: comic.title,
                    description: comic.description,
                    creator: {
                      '@type': 'Person',
                      name: comic.author,
                    },
                    genre: comic.category,
                    inLanguage: 'zh-CN',
                  },
                  generateBreadcrumbLD(breadcrumbItems),
                ]),
              }}
            />
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}